import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogIn, Mail, MailPlus, Trash2, UserPlus } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";

export default function Businesses() {
  const [businesses, setBusinesses] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newBiz, setNewBiz] = useState({ name: "", owner_name: "", email: "", plan_id: "" });
  const [creds, setCreds] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailBiz, setEmailBiz] = useState(null);
  const [newEmail, setNewEmail] = useState("");

  const changeEmail = async () => {
    try {
      const { data } = await api.put(`/admin/businesses/${emailBiz.id}/email`, { new_email: newEmail });
      toast.success(`Login email updated to ${data.email}`);
      setEmailBiz(null);
      setNewEmail("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = () => {
    api.get("/admin/businesses").then(({ data }) => setBusinesses(data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/businesses/${id}`, body);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const impersonate = async (id) => {
    try {
      const { data } = await api.post(`/admin/businesses/${id}/impersonate`);
      localStorage.setItem("impersonate_token", data.token);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteBusiness = async (b) => {
    if (!window.confirm(`Permanently delete "${b.name}"? This removes the business, its users, forms, submissions, conversations and all related data. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/businesses/${b.id}`);
      toast.success(`Deleted ${b.name}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendLogin = async (id) => {
    try {
      const { data } = await api.post(`/admin/businesses/${id}/send-login`);
      setCreds({ email: data.email, password: data.temp_password, email_status: data.email_status });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createBusiness = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/businesses", { ...newBiz, plan_id: newBiz.plan_id || null });
      setCreds({ email: newBiz.email, password: data.temp_password, email_status: data.email_status });
      setShowAdd(false);
      setNewBiz({ name: "", owner_name: "", email: "", plan_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-businesses-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black">Businesses</h1>
          <p className="text-sm text-neutral-500 mt-1">Change plans, waive fees, suspend accounts, log in as a business — or add one manually.</p>
        </div>
        <Button className="rounded-full transition-transform duration-150 hover:-translate-y-0.5" onClick={() => setShowAdd(true)} data-testid="add-business-button">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add business
        </Button>
      </div>

      <div className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-[#E5E5E5] text-left text-xs uppercase tracking-widest text-neutral-400">
              <th className="px-5 py-3">Business</th>
              <th className="px-5 py-3">Plan</th>
              <th className="px-5 py-3">Fees waived</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Usage</th>
              <th className="px-5 py-3">Joined</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]">
            {!businesses ? (
              <tr><td className="px-5 py-6 text-neutral-400" colSpan={7}>Loading…</td></tr>
            ) : businesses.length === 0 ? (
              <tr><td className="px-5 py-6 text-neutral-400" colSpan={7} data-testid="businesses-empty">No businesses yet.</td></tr>
            ) : (
              businesses.map((b) => (
                <tr key={b.id} data-testid={`business-row-${b.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="font-medium">{b.name}</div>
                    <div className="text-xs text-neutral-400">{b.email}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <Select value={b.plan_id || ""} onValueChange={(v) => patch(b.id, { plan_id: v })}>
                      <SelectTrigger className="w-32 h-9" data-testid={`business-plan-select-${b.id}`}>
                        <SelectValue placeholder="Plan" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {plans.map((p) => (
                          <SelectItem key={p.id} value={p.id} data-testid={`business-plan-option-${b.id}-${p.id}`}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-5 py-3.5">
                    <Switch checked={!!b.fees_waived} onCheckedChange={(v) => patch(b.id, { fees_waived: v })} data-testid={`business-waive-${b.id}`} />
                  </td>
                  <td className="px-5 py-3.5">
                    <Select value={b.status} onValueChange={(v) => patch(b.id, { status: v })}>
                      <SelectTrigger className="w-32 h-9" data-testid={`business-status-select-${b.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-5 py-3.5 text-neutral-500">
                    {b.form_count} forms · {b.submission_count} subs
                  </td>
                  <td className="px-5 py-3.5 text-neutral-500">{new Date(b.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <Button variant="outline" size="sm" className="rounded-full mr-2" onClick={() => { setEmailBiz(b); setNewEmail(b.email); }} data-testid={`business-change-email-${b.id}`}>
                      <MailPlus className="h-3.5 w-3.5 mr-1" /> Email
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-full mr-2" onClick={() => sendLogin(b.id)} data-testid={`business-send-login-${b.id}`}>
                      <Mail className="h-3.5 w-3.5 mr-1" /> Send login
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => impersonate(b.id)} data-testid={`business-impersonate-${b.id}`}>
                      <LogIn className="h-3.5 w-3.5 mr-1" /> Log in as
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 ml-1" onClick={() => deleteBusiness(b)} data-testid={`business-delete-${b.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-white max-w-md" data-testid="add-business-dialog">
          <DialogHeader>
            <DialogTitle>Add a business</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Business name</Label>
              <Input value={newBiz.name} onChange={(e) => setNewBiz({ ...newBiz, name: e.target.value })} data-testid="add-business-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Owner name</Label>
              <Input value={newBiz.owner_name} onChange={(e) => setNewBiz({ ...newBiz, owner_name: e.target.value })} data-testid="add-business-owner-input" />
            </div>
            <div className="space-y-2">
              <Label>Owner email</Label>
              <Input type="email" value={newBiz.email} onChange={(e) => setNewBiz({ ...newBiz, email: e.target.value })} data-testid="add-business-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Plan (defaults to Free)</Label>
              <Select value={newBiz.plan_id} onValueChange={(v) => setNewBiz({ ...newBiz, plan_id: v })}>
                <SelectTrigger data-testid="add-business-plan-select">
                  <SelectValue placeholder="Free (default)" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`add-business-plan-option-${p.id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-neutral-400">
              A temporary password is generated and emailed to the owner. You'll also see it once, here.
            </p>
            <Button
              onClick={createBusiness}
              disabled={saving || !newBiz.name || !newBiz.owner_name || !newBiz.email}
              className="w-full rounded-full"
              data-testid="add-business-submit"
            >
              {saving ? "Creating…" : "Create & send login"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailBiz} onOpenChange={(open) => !open && setEmailBiz(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="change-email-dialog">
          <DialogHeader>
            <DialogTitle>Change login email — {emailBiz?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New owner email</Label>
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="change-email-input" />
              <p className="text-xs text-neutral-400">The owner logs in with this email. Their password stays the same.</p>
            </div>
            <Button
              onClick={changeEmail}
              disabled={!newEmail.includes("@") || newEmail === emailBiz?.email}
              className="w-full rounded-full"
              data-testid="change-email-submit"
            >
              Update email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!creds} onOpenChange={(open) => !open && setCreds(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle>Login credentials</DialogTitle>
          </DialogHeader>
          {creds && (
            <div className="space-y-4">
              <div className="bg-neutral-50 border border-[#E5E5E5] rounded-xl p-4 text-sm space-y-1.5">
                <div>Email: <b data-testid="creds-email">{creds.email}</b></div>
                <div>Temporary password: <b data-testid="creds-password">{creds.password}</b></div>
              </div>
              <p className={`text-xs ${creds.email_status === "sent" ? "text-emerald-700" : "text-amber-700"}`} data-testid="creds-email-status">
                {creds.email_status === "sent"
                  ? "The welcome email was sent to the owner."
                  : "SMTP is not configured, so no email was delivered — share these credentials manually. (Recorded in the Email Log.)"}
              </p>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => {
                  navigator.clipboard.writeText(`Email: ${creds.email}\nPassword: ${creds.password}`);
                  toast.success("Copied to clipboard");
                }}
                data-testid="creds-copy-button"
              >
                Copy credentials
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
